import { getPackageDotJson } from './clack-utils.js';
import type { InstallerOptions } from './types.js';
import fg from 'fast-glob';
import { isHumanMode } from './interaction-mode.js';

/**
 * Compatibility wrapper for legacy call sites.
 *
 * Interaction mode now owns prompt/browser behavior. Keep this helper while
 * call sites migrate, but do not reimplement env/TTY detection here.
 */
export function isNonInteractiveEnvironment(): boolean {
  return !isHumanMode();
}

export function readEnvironment(): Record<string, unknown> {
  const prefix = 'WORKOS_INSTALLER_';
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(process.env)) {
    if (key.startsWith(prefix)) {
      const envKey = key.slice(prefix.length).toLowerCase();
      result[envKey] = value;
    }
  }

  return result;
}

export async function detectEnvVarPrefix(options: InstallerOptions): Promise<string> {
  const packageJson = await getPackageDotJson(options);

  const deps = { ...packageJson.dependencies, ...packageJson.devDependencies };
  const has = (name: string) => name in deps;
  const hasAnyFile = async (patterns: string[]) => {
    const matches = await fg(patterns, {
      cwd: options.installDir,
      absolute: false,
      onlyFiles: true,
      ignore: ['**/node_modules/**'],
    });
    return matches.length > 0;
  };

  // --- Next.js
  if (has('next') || (await hasAnyFile(['**/next.config.{js,ts,mjs,cjs}']))) {
    return 'NEXT_PUBLIC_';
  }

  // --- Create React App
  if (has('react-scripts') || has('create-react-app') || (await hasAnyFile(['**/config-overrides.js']))) {
    return 'REACT_APP_';
  }

  // --- Vite (vanilla, TanStack, Solid, etc.)
  // Note: Vite does not need PUBLIC_ but we use it to follow the docs, to improve the chances of an LLM getting it right.
  if (has('vite') || (await hasAnyFile(['**/vite.config.{js,ts,mjs,cjs}']))) {
    return 'VITE_PUBLIC_';
  }

  // --- SvelteKit
  if (has('@sveltejs/kit') || (await hasAnyFile(['**/svelte.config.{js,ts}']))) {
    return 'PUBLIC_';
  }

  // --- TanStack Start (uses Vite)
  if (has('@tanstack/start') || (await hasAnyFile(['**/tanstack.config.{js,ts}']))) {
    return 'VITE_PUBLIC_';
  }

  // --- SolidStart (uses Vite)
  if (has('solid-start') || (await hasAnyFile(['**/solid.config.{js,ts}']))) {
    return 'VITE_PUBLIC_';
  }

  // --- Astro
  if (has('astro') || (await hasAnyFile(['**/astro.config.{js,ts,mjs}']))) {
    return 'PUBLIC_';
  }

  // We default to Vite if we can't detect a specific framework, since it's the most commonly used.
  return 'VITE_PUBLIC_';
}
